import React, { useState, useEffect, useRef } from 'react';
import { StyleSheet, Image, View, useWindowDimensions, TouchableOpacity, ActivityIndicator } from 'react-native';
import { Col, Row, useDynamicResponsiveValue, Text, Scroll } from 'react-quick-style-components';
import { useNavFunc } from 'navigation';
import { IScreen } from 'type';
import { ASSETS, COLOR, fSize, ICON, SCREEN_NAME } from 'const';
import { Footer, Header, Card, Button, Input, ChatBox, ChartMultiple, TableWithAction, QuickMessage, Table, CaskIOwn, Breadcrumbs, EsignModal } from "components";
import { BlurView } from 'expo-blur';
import Store from 'store';
import { TimeHelper, VarHelper } from 'helpers';
import _ from 'lodash';
import { saveAs } from 'file-saver';
import { MaterialIcons } from '@expo/vector-icons';

let XLSX;

const YourCasks: IScreen = () => {
  const [{ email, name, firebaseUser }] = Store.User.createStore();
  const [{ }, mApi] = Store.Message.createStore();
  const [{ listNews, listPages }, iApi] = Store.Info.createStore();

  const [{ listDocs, listAvailableCasks, listClients, saleManagerData }, cApi] = Store.Client.createStore();
  const [{ selectedCasks, isModalVisible }, eApi] = Store.Esign.createStore();
  
  const [signedCaskIds, setSignedCaskIds] = useState<Set<string>>(new Set());
  const [loadingSignedCasks, setLoadingSignedCasks] = useState(true);
  const [caskBatchMap, setCaskBatchMap] = useState<Map<string, any>>(new Map()); // Map cask ID to batch info
  const [batchDetailModal, setBatchDetailModal] = useState<any>(null); // Batch info for modal
  const [batchPdfUrl, setBatchPdfUrl] = useState<string>(''); // Signed URL for PDF preview
  const [loadingPdf, setLoadingPdf] = useState(false);

  // Get client email and name for E-signature - support personate mode like Header
  const isPersonating = !!saleManagerData.account['Personate Email'];
  const esignClientEmail = isPersonating ? saleManagerData.account['Personate Email'] : email;
  const esignClientName = (() => {
    if (isPersonating) {
      const findClient = saleManagerData.clients.find(val => val.Email === saleManagerData.account['Personate Email']);
      return !!findClient ? findClient.Customer : saleManagerData.account['Personate Email'];
    }
    return name || (firebaseUser?.displayName) || '';
  })();

  const { navigate } = useNavFunc();
  const { width, height } = useWindowDimensions();
  const rV = useDynamicResponsiveValue();
  const BLUR_WIDTH = rV({ xs: (width - 80) / 2, md: (width - 160) / 3 });
  const BLUR_HEIGHT = rV({ xs: (width - 80) / 2, md: (width - 160) * 0.6 / 3 });
  const ITEM_WIDTH = rV({ xs: (width - 50), lg: (width - 110) / 3 });
  const ROW_MARGIN = rV({ xs: 25, md: 35, lg: 45 });
  const COLUMN_MARGIN = rV({ xs: 5, lg: 10 });

  useEffect(() => {
    if (!XLSX) {
      import('xlsx').then(xlsx => {
        XLSX = xlsx;
      });
    }
  }, []);

  useEffect(() => {
    iApi.getListEvents();
    iApi.getListNews();
    cApi.getListClients();
    cApi.getListAvailableCasks();
    iApi.getListPages();
    cApi.getListDocs();
    loadSignedCasks();
  }, []);

  const loadSignedCasks = async () => {
    setLoadingSignedCasks(true);
    
    // Load all esign records (regardless of status)
    const [res, err] = await eApi.getEsignRequests({
      limit: 1000, // Get all records
      offset: 0,
    });

    if (res && res.success && res.data) {
      const signedCasks = new Set<string>();
      const batchMap = new Map<string, any>();
      
      // Collect all cask IDs from ALL esign records (any status)
      res.data.forEach(record => {
        if (Array.isArray(record.cask_ids)) {
          record.cask_ids.forEach(caskId => {
            signedCasks.add(String(caskId));
            
            // Map cask ID to batch info (keep the latest/most recent batch)
            const existingBatch = batchMap.get(String(caskId));
            if (!existingBatch || new Date(record.created_at) > new Date(existingBatch.created_at)) {
              batchMap.set(String(caskId), {
                batchId: record.id,
                status: record.status,
                clientName: record.client_name,
                clientEmail: record.client_email,
                createdAt: record.created_at,
                signedAt: record.signed_at,
                pdfPath: record.signed_pdf_path || record.pdf_path,
              });
            }
          });
        }
      });
      
      setSignedCaskIds(signedCasks);
      setCaskBatchMap(batchMap);
      console.log('Loaded all cask IDs in esign records:', Array.from(signedCasks));
      console.log('Cask to Batch mapping:', batchMap);
    }
    
    setLoadingSignedCasks(false);
  };

  const chartRef = useRef<{ setActiveTab: Function }>();

  const pageData = listPages.find(val => val.Title === 'Account Summary');
  const pageDataImages = VarHelper.imgApiStringifyGetAll(pageData?.Images);

  const myAM = listClients.length > 0 ? listClients[0]['Account Manager'] : '';

  const messageAdmin = async (item) => {
    // console.log('item', item);
    // console.log('myAM', myAM);
    // { Region: 'Highlands', Cask Type: 'Barel', Age: 5, Price: '5-10' }
    const [res, err] = await mApi.sendMessage({
      subject: 'Request cask info',
      from: email,
      message: `${name} (${email}) has requested info about this cask: Region ${item.Region} | type ${item['Cask Type']} | Age ${item.Age} | Price ${item.Price}`,
      am: myAM,
    });
    alert('Thank you, your request has been sent');
  }

  const ScreenContainer = React.Fragment;

  const { portfolioValue } = (() => {
    if (listClients.length === 0 || !email) return { portfolioValue: 0 };
    const filterClient = listClients.filter(val => val.Email === email);
    // console.log('filterClient', filterClient);
    // if (filterClient.length > 0) return {
    //   portfolioValue: +filterClient[0]['Portfolio'],
    // }
    if (filterClient.length > 0) return filterClient.reduce((acc, val) => {
      return {
        portfolioValue: acc.portfolioValue + +val['Portfolio'],
      }
    }, { portfolioValue: 0 });
    return {
      portfolioValue: 0,
    };
  })();

  const numberOfRegions = _.uniqWith(listClients.filter(val => val.Email === email && !!val['Region']), _.isEqual).length;
  const listDistilleries = _.uniqWith(listClients.filter(val => val.Email === email && !!val['Distillery']).map(val => val.Distillery), _.isEqual);

  const { myCommision } = cApi.computeMyComission();

  // const filterClient = listClients.filter(val => val.Email === email).sort((a, b) => a['Region'] < b['Region'] ? -1 : 1);
  const filterClient = listClients.filter(val => val.Email === email).sort((a, b) => {
    return TimeHelper.compare(a['Purchase Date'], b['Purchase Date'], 'DD/MM/YYYY');
  });

  const isAllSelected = filterClient.length > 0 && selectedCasks.length === filterClient.length;
  const isSomeSelected = selectedCasks.length > 0 && selectedCasks.length < filterClient.length;

  const handleSelectAll = () => {
    // Only select casks that are not signed/completed
    const selectableCasks = filterClient.filter(cask => !isCaskSigned(cask));
    eApi.selectAllCasks(selectableCasks, !isAllSelected);
  };

  const handleToggleCask = (cask) => {
    // Don't allow toggling if cask is already signed/completed
    if (isCaskSigned(cask)) {
      return;
    }
    eApi.toggleCaskSelection(cask);
  };

  const isCaskSigned = (cask) => {
    return signedCaskIds.has(String(cask.id));
  };

  const isCaskSelected = (cask) => {
    // Use 'id' field as primary identifier
    return selectedCasks.some(item => item.id === cask.id);
  };

  const handleOpenEsignModal = () => {
    console.log('=== handleOpenEsignModal called ===');
    console.log('selectedCasks:', selectedCasks);
    console.log('selectedCasks.length:', selectedCasks.length);
    console.log('isModalVisible BEFORE:', isModalVisible);
    
    // Validation 1: Check if at least one cask is selected
    if (selectedCasks.length === 0) {
      alert('Please select at least one cask');
      return;
    }

    // Validation 2: Check if all selected casks have required fields
    const invalidCasks = selectedCasks.filter(cask => !cask.id);
    if (invalidCasks.length > 0) {
      alert('Some selected casks are missing required information. Please refresh and try again.');
      console.error('Invalid casks:', invalidCasks);
      return;
    }

    // Validation 3: Check if client email is available
    if (!email) {
      alert('Client email is required. Please log in again.');
      return;
    }
    
    console.log('Opening modal...');
    eApi.openModal();
    
    // Check after a short delay to see if state updated
    setTimeout(() => {
      console.log('isModalVisible AFTER:', isModalVisible);
    }, 100);
  };

  const handleCloseEsignModal = () => {
    eApi.closeModal();
  };

  const handleSignSuccess = (caskIds: string[]) => {
    // Reload data without full page refresh
    cApi.getListClients();
    loadSignedCasks();
  };

  const handleSaveSuccess = () => {
    // Reload data without full page refresh
    cApi.getListClients();
    loadSignedCasks();
  };

  const handleOpenBatchDetail = async (batchInfo: any) => {
    setBatchDetailModal(batchInfo);
    setBatchPdfUrl('');
    
    if (batchInfo.pdfPath) {
      setLoadingPdf(true);
      // Generate signed URL for PDF
      const [res, err] = await cApi.genAssetsLink({ id: batchInfo.batchId, type: 'esign' });
      setLoadingPdf(false);
      
      if (res && res.data) {
        setBatchPdfUrl(res.data);
      }
    }
  };

  const handleCloseBatchDetail = () => {
    setBatchDetailModal(null);
    setBatchPdfUrl('');
  };

  const exportExcel = () => {
    var wb = XLSX.utils.book_new();
    var ws = XLSX.utils.json_to_sheet([
      {
        Name: 'Portfolio',
        Value: VarHelper.money(portfolioValue),
      },
      {
        Name: 'No. of casks I own',
        Value: filterClient.length,
      },
      {
        Name: 'Your Distilleries',
        Value: listDistilleries.length,
      },
    ]);
    XLSX.utils.book_append_sheet(wb, ws, "Account Summary");

    const yourCasks = filterClient.map(val => ({
      ...val,
      ['AYS']: TimeHelper.format(val['AYS']),
      ['Cask price']: VarHelper.money(val['Unit Price']),
      ['Portfolio']: VarHelper.money(val['Portfolio']),
      ['Purchased Date']: TimeHelper.format(val['Purchased Date']),
      ['Cask No']: val['Cask NO'],
      ['RLA']: val['OLA/RLA'],
      // ['KYC']: val['KYC'],
      'Recommended Re-gauge Date': TimeHelper.format(val['Recommended Re-gauge Date']),
      'Storage Expiry': TimeHelper.format(val['Storage Expired']),
    }))
    var ws2 = XLSX.utils.json_to_sheet(yourCasks);
    XLSX.utils.book_append_sheet(wb, ws2, "Your Casks");

    var wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });

    saveAs(new Blob([wbout], { type: "application/octet-stream" }), `export-account-summary-${new Date().getTime()}.xlsx`);
  };

  const renderRow = (arrayOfComp) => {
    return (
      <Row
        style={{ flexWrap: 'wrap' }}
        marginBottom={10}
        marginTop20 stretch={rV({ xs: undefined, md: true })}
        marginHorizontal={ROW_MARGIN}
      >
        {arrayOfComp.map((comp, index) => (
          <React.Fragment key={`row-item-${index}`}>{comp}</React.Fragment>
        ))}
      </Row>
    )
  }

  const renderCasksIOwn = () => {
    return (
      <Card
        title={"Casks I own"}
        width={rV({ xs: ITEM_WIDTH - 20, lg: ITEM_WIDTH * 3 - 10 })}
        marginHorizontal={COLUMN_MARGIN} marginBottom10 iconRigth={<ICON.IconPie />}
      >
        <CaskIOwn ref={chartRef} />
      </Card>
    )
  }

  const renderYourCask = () => {
    // Show loading while checking signed casks
    if (loadingSignedCasks) {
      return (
        <Col width={rV({ xs: ITEM_WIDTH - 20, lg: ITEM_WIDTH * 3 - 10 })}
          marginHorizontal={COLUMN_MARGIN} marginBottom10
          center paddingVertical={60}>
          <ActivityIndicator size="large" color={COLOR.PRIMARY} />
          <Text marginTop={20} fontSize={16}>Loading casks...</Text>
        </Col>
      );
    }

    return (
      <Col width={rV({ xs: ITEM_WIDTH - 20, lg: ITEM_WIDTH * 3 - 10 })}
        marginHorizontal={COLUMN_MARGIN} marginBottom10>
        {!!filterClient && filterClient.length === 0 ? (
          <Col>
            <Text body1>No record found</Text>
          </Col>
        ) : (
          <Table
            columns={[
              { 
                key: 'checkbox', 
                title: () => (
                  <TouchableOpacity onPress={handleSelectAll}>
                    <MaterialIcons 
                      name={isAllSelected ? "check-box" : isSomeSelected ? "indeterminate-check-box" : "check-box-outline-blank"} 
                      size={20} 
                      color="white" 
                    />
                  </TouchableOpacity>
                ), 
                flex: 0, 
                width: 70, 
                center: true,
                padding: 20,
              },
              { key: 'Spirit', title: 'Spirit', flex: 1, center: true },
              { key: 'Distillery', title: 'Distillery', flex: 1, center: true },
              { key: 'Region', title: 'Region', flex: 1, center: true },
              { key: 'AYS', title: 'AYS', flex: 1, center: true, tooltip: 'Age of Youngest Spirit' },
              { key: 'Cask Type', title: 'Cask Type', flex: 1, center: true },
              { key: 'OLA', title: 'OLA', flex: 1, center: true, tooltip: 'Original Litres of Alcohol' },
              { key: 'RLA', title: 'RLA', flex: 1, center: true, tooltip: 'Regauged Litres of Alcohol' },
              { key: 'ABV', title: 'ABV', flex: 1, center: true, tooltip: 'Alcohol By Volume' },
              { key: 'Portfolio', title: 'Price', flex: 1, center: true },
              { key: 'Purchase Date', title: 'Purchase Date', flex: 1, center: true },
              { key: 'Transfer Batch', title: 'Transfer Batch', flex: 1, center: true },
              { key: 'Cask NO', title: 'Cask NO', flex: 1, center: true, showInDetail: true },
              { key: 'Location', title: 'Location', flex: 1, center: true, showInDetail: true },
              { key: 'Age', title: 'Age', showInDetail: true },
              { key: 'Cask Price', title: 'Cask Price', showInDetail: true },
              { key: 'Brand Owner', title: 'Brand Owner', showInDetail: true },
              { key: 'Recommended Re-gauge Date', title: 'Recommended Re-gauge Date', showInDetail: true },
              { key: 'Storage Expired', title: 'Storage Expiry', showInDetail: true },
              { key: 'Invested', title: 'Invested', showInDetail: true },
              { key: 'Investment Tier', title: 'Investment Tier', showInDetail: true },
              { key: 'AMC Renewal', title: 'AMC Renewal', showInDetail: true },
              { key: 'Valuation Date', title: 'Valuation Date', showInDetail: true },
              { key: 'Months Held', title: 'Months Held', showInDetail: true },
              { key: 'Valuation', title: 'Valuation', showInDetail: true },
              { key: 'Growth', title: 'Growth', showInDetail: true },
              { key: 'Gross %', title: 'Gross %', showInDetail: true },
              { key: 'VA Fee', title: 'VA Fee', showInDetail: true },
              { key: 'Net %', title: 'Net %', showInDetail: true },
              { key: 'Net', title: 'Net', showInDetail: true },
              { key: 'ROI', title: 'ROI', showInDetail: true },
              { key: 'Sold Casks', title: 'Sold Casks', showInDetail: true },
              { key: 'Bottle Yield', title: 'Bottle Yield', showInDetail: true },
              { key: 'Bottle £', title: 'Bottle £', showInDetail: true },
            ]}
            withoutNumberColumn
            data={filterClient.map(val => ({
              ...val,
              ['checkbox']: () => {
                const isDisabled = isCaskSigned(val);
                return (
                  <TouchableOpacity 
                    onPress={() => handleToggleCask(val)}
                    disabled={isDisabled}
                    style={{ opacity: isDisabled ? 0.4 : 1 }}
                  >
                    <MaterialIcons 
                      name={isCaskSelected(val) ? "check-box" : "check-box-outline-blank"} 
                      size={20} 
                      color={isDisabled ? '#999' : COLOR.MAIN} 
                    />
                  </TouchableOpacity>
                );
              },
              ['Spirit']: val['Spirit'],
              ['AYS']: val['AYS'],
              ['Cask Price']: VarHelper.money(val['Portfolio']),
              ['Portfolio']: VarHelper.money(val['Portfolio']),
              ['Invested']: VarHelper.money(val['Invested']),
              ['Bottle £']: VarHelper.money(val['Bottle £']),
              ['ROI']: VarHelper.money(val['ROI']),
              ['Growth']: VarHelper.money(val['Growth']),
              ['VA Fee']: VarHelper.money(val['VA Fee']),
              ['Net']: VarHelper.money(val['Net']),
              ['Valuation']: VarHelper.money(val['Valuation']),
              ['Purchase Date']: val['Purchase Date'],
              ['Cask NO']: val['Cask No'],
              ['OLA/RLA']: val['OLA/RLA'],
              ['Location']: val['Location'],
              'Recommended Re-gauge Date': val['Recommended Re-gauge Date'],
              'Storage Expired': val['Storage Expired'] ? TimeHelper.format(val['Storage Expired'], 'DD/MM/YYYY') : '',
              ['Transfer Batch']: () => {
                const batchInfo = caskBatchMap.get(String(val.id));
                if (!batchInfo) {
                  return <Text fontSize={13} color="#999">Not transferred</Text>;
                }
                return (
                  <a
                    onClick={() => handleOpenBatchDetail(batchInfo)}
                    style={{
                      cursor: 'pointer',
                      color: COLOR.MAIN,
                      textDecoration: 'underline',
                      fontSize: 13,
                    }}
                    title="View batch details"
                  >
                    Batch #{batchInfo.batchId}
                  </a>
                );
              },
            }))}
          />
        )}
      </Col>
    )
  }

  const renderAvailableCasks = () => {
    return (
      <Col width={rV({ xs: ITEM_WIDTH - 20, lg: ITEM_WIDTH * 3 - 10 })}
        marginHorizontal={COLUMN_MARGIN} marginBottom10>
        <Card title={"Available Casks - Subject to being sold"} height100p>
          <TableWithAction
            titleBlackBg
            columns={[
              { key: 'Distillery', title: 'DISTILLERY', flex: 1, center: true },
              { key: 'Region', title: 'REGION', flex: 1, center: true },
              { key: 'Cask Type', title: 'CASK TYPE', flex: 1, center: true },
              { key: 'Cask number', title: 'CASK#', flex: 1, center: true },
              { key: 'Age', title: 'AGE', flex: 1, center: true },
              { key: 'Price', title: 'CASK PRICE', flex: 1, center: true },
            ]}
            data={listAvailableCasks}
            onPressAction={(item, i) => messageAdmin(item)}
            breakpointToScroll="sm"
            btnTitle="Request Info"
          />
        </Card>

      </Col>
    )
  }

  const scrollToChart = (activeTab) => {
    window.scrollTo({ left: 0, top: 319 });
    if (chartRef.current?.setActiveTab) chartRef.current?.setActiveTab(activeTab);
  }

  const renderSquare = ({ onPress, image, title, value }) => {
    return (
      <Col onPress={onPress} overflowH marginHorizontal={rV({ xs: 5, md: 10 })} marginBottom10>
        <Image source={image} style={{ width: BLUR_WIDTH, height: BLUR_HEIGHT, resizeMode: 'cover' }} />
        <Col absoluteFill>
          <Text height40 marginTop={BLUR_HEIGHT / 3.5} center bold color={COLOR.WHITE} fontSize={fSize(rV, 26)}>
            {title}
          </Text>
          <Row minHeight40 justifyContent="center">
            <Text center bold color={COLOR.WHITE} fontSize={String(value).length <= 5 ? 50 : 30}>
              {value}
            </Text>
          </Row>
        </Col>
      </Col>
    );
  }

  return (
    <Col flex1 backgroundColor={COLOR.BG}>
      <ScreenContainer>
        <Header />
        <Col flex1>
          <Row justifyContent="space-between">
            <Breadcrumbs
              title="Your Casks"
              style={{
                marginLeft: ROW_MARGIN + COLUMN_MARGIN,
              }}
            />
            <Row marginTop20 marginRight={ROW_MARGIN + COLUMN_MARGIN} alignItems="center">
              {selectedCasks.length > 0 && (
                <div onClick={handleOpenEsignModal} style={{ marginRight: 30, cursor: 'pointer' }}>
                  <Text fontSize={15} letterSpacing={0.3}>Delivery Order - Send for E-sign ({selectedCasks.length})</Text>
                </div>
              )}
              <div onClick={exportExcel} style={{ cursor: 'pointer' }}>
                <Text fontSize={15} letterSpacing={0.3}>Export Data</Text>
              </div>
            </Row>
          </Row>

          {renderRow([
            renderYourCask(),
          ])}
        </Col>
        <Footer />
      </ScreenContainer>
      <ChatBox />
      <EsignModal
        visible={isModalVisible}
        onClose={handleCloseEsignModal}
        selectedCasks={selectedCasks}
        clientEmail={esignClientEmail}
        clientName={esignClientName}
        onSignSuccess={handleSignSuccess}
        onSaveSuccess={handleSaveSuccess}
      />
      
      {/* Batch Detail Modal */}
      {batchDetailModal && (
        <Col
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onPress={handleCloseBatchDetail}
        >
          <Col
            style={{
              backgroundColor: 'white',
              borderRadius: 8,
              width: rV({ xs: width - 40, md: width * 0.9, lg: width * 0.85, xl: 1400 }),
              height: rV({ xs: height - 80, md: height * 0.85, lg: height * 0.9 }),
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
            onPress={(e) => e.stopPropagation()}
          >
            {/* Fixed Header */}
            <Col
              style={{
                padding: 20,
                borderBottom: '1px solid #ddd',
                backgroundColor: 'white',
              }}
            >
              <Row style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                <Text fontSize={20} bold>Transfer Batch #{batchDetailModal.batchId}</Text>
                <button
                  onClick={handleCloseBatchDetail}
                  style={{
                    border: 'none',
                    background: 'none',
                    fontSize: 28,
                    cursor: 'pointer',
                    color: '#666',
                    lineHeight: 1,
                  }}
                >
                  ×
                </button>
              </Row>
            </Col>
            
            {/* Scrollable Content */}
            <Row style={{flex: 1, display: 'flex', gap: 20, padding: 20 }}>
              {/* Left: Info (25% width) */}
              <Col style={{ width: '25%', height: '100%', overflowY: 'auto' }}>
                <Text fontSize={16} bold marginBottom={20} style={{ color: COLOR.MAIN }}>
                  Batch Information
                </Text>
                
                <Col marginBottom={15}>
                  <Text fontSize={12} bold color="#666" marginBottom={5}>STATUS</Text>
                  <Text fontSize={14} style={{ textTransform: 'capitalize' }}>
                    {batchDetailModal.status}
                  </Text>
                </Col>
                
                <Col marginBottom={15}>
                  <Text fontSize={12} bold color="#666" marginBottom={5}>CLIENT NAME</Text>
                  <Text fontSize={14}>{batchDetailModal.clientName}</Text>
                </Col>
                
                <Col marginBottom={15}>
                  <Text fontSize={12} bold color="#666" marginBottom={5}>CLIENT EMAIL</Text>
                  <Text fontSize={14}>{batchDetailModal.clientEmail}</Text>
                </Col>
                
                <Col marginBottom={15}>
                  <Text fontSize={12} bold color="#666" marginBottom={5}>CREATED DATE</Text>
                  <Text fontSize={14}>
                    {batchDetailModal.createdAt ? new Date(batchDetailModal.createdAt).toLocaleString('en-GB') : 'N/A'}
                  </Text>
                </Col>
                
                {batchDetailModal.signedAt && (
                  <Col marginBottom={15}>
                    <Text fontSize={12} bold color="#666" marginBottom={5}>SIGNED DATE</Text>
                    <Text fontSize={14}>
                      {new Date(batchDetailModal.signedAt).toLocaleString('en-GB')}
                    </Text>
                  </Col>
                )}
              </Col>
              
              {/* Right: PDF Preview (75% width) */}
              <Col style={{ flex: 1, height: '100%'}}>
                {/* Title Section */}
                <Col style={{ marginBottom: 20 }}>
                  <Text fontSize={16} bold style={{ color: COLOR.MAIN }}>
                    Document Preview
                  </Text>
                </Col>
                
                {/* PDF Content Section - takes remaining height */}
                <Col style={{ flex: 1 }}>
                  {loadingPdf ? (
                    <Col center style={{ height: '100%' }}>
                      <ActivityIndicator size="large" color={COLOR.MAIN} />
                      <Text marginTop={20} fontSize={14} color="#666">Loading PDF...</Text>
                    </Col>
                  ) : batchPdfUrl ? (
                    <iframe
                      src={batchPdfUrl}
                      style={{
                        width: '100%',
                        height: '100%',
                        border: 'none',
                        borderRadius: 4,
                        backgroundColor: 'white',
                      }}
                      title="PDF Preview"
                    />
                  ) : (
                    <Col center style={{ height: '100%' }}>
                      <Text fontSize={14} color="#999">PDF not available</Text>
                    </Col>
                  )}
                </Col>
              </Col>
            </Row>
            
            {/* Fixed Footer */}
            <Col
              style={{
                padding: 20,
                paddingTop: 15,
                paddingBottom: 20,
                borderTop: '1px solid #ddd',
                backgroundColor: 'white',
              }}
            >
              <Row style={{ justifyContent: 'flex-end' }}>
                <button
                  onClick={handleCloseBatchDetail}
                  style={{
                    padding: '12px 30px',
                    border: 'none',
                    borderRadius: 4,
                    backgroundColor: COLOR.MAIN,
                    color: 'white',
                    cursor: 'pointer',
                    fontSize: 14,
                    fontWeight: 'bold',
                  }}
                >
                  Close
                </button>
              </Row>
            </Col>
          </Col>
        </Col>
      )}
    </Col>
  );
};

YourCasks.routeInfo = {
  title: 'Your Casks',
  path: '/your-casks',
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  blurredImage: {
    width: 192,
    height: 192,
  },
  nonBlurredContent: {
    alignItems: 'center',
    justifyContent: 'center',
  },
});

export default YourCasks;